import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, GridFour, TextT, FilmStrip, Sliders, Plus, X, Trash, MagicWand } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { fileUrl } from "../lib/api";

const MODES = [
  { k: "collage", label: "Multi-Photo", icon: GridFour },
  { k: "overlay", label: "Text & Stickers", icon: TextT },
  { k: "slideshow", label: "Photo → Video", icon: FilmStrip },
  { k: "filter", label: "Filters", icon: Sliders },
  { k: "recap", label: "Recap", icon: MagicWand },
];
const LAYOUTS = ["grid", "row", "column"];
const ASPECTS = ["1:1", "9:16", "4:5", "16:9"];
const TRANSITIONS = [{ k: "none", l: "None" }, { k: "crossfade", l: "Crossfade" }, { k: "kenburns", l: "Ken Burns" }];
const COLORS = ["#FFFFFF", "#070A10", "#C66A4A", "#FF5C00", "#FFD93D", "#FF7AC6", "#4ADE80"];
const FONTS = [{ k: "poppins", l: "Bold" }, { k: "anton", l: "Impact" }, { k: "mono", l: "Mono" }];

function aspectRatio(a) {
  return a === "9:16" ? "9 / 16" : a === "16:9" ? "16 / 9" : a === "4:5" ? "4 / 5" : "1 / 1";
}

/** Web Media Studio — mirrors the mobile creative suite: collage, text+sticker
 *  overlays, photo→video with transitions, and filters with a strength slider.
 *  All processing runs on the shared backend (/api/media/*). */
export default function MediaStudio() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [mode, setMode] = useState(MODES.some((m) => m.k === sp.get("tab")) ? sp.get("tab") : "collage");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { path, type: 'image'|'video' }
  const [caption, setCaption] = useState("");

  // shared file state
  const [files, setFiles] = useState([]); // {url, file}
  const fileRef = useRef(null);

  // collage / slideshow options
  const [layout, setLayout] = useState("grid");
  const [aspect, setAspect] = useState("1:1");
  const [perSec, setPerSec] = useState(2.5);
  const [transition, setTransition] = useState("none");

  // filter options
  const [filters, setFilters] = useState([]);
  const [filterKey, setFilterKey] = useState("original");
  const [strength, setStrength] = useState(1);
  const [retouch, setRetouch] = useState(false);

  // overlay options
  const [stickers, setStickers] = useState([]);
  const [layers, setLayers] = useState([]); // {id,type,text?,emoji?,url?,preview?,color,font,xf,yf,sizef}
  const [selId, setSelId] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const canvasRef = useRef(null);

  // GIPHY animated stickers / GIFs
  const [gifOn, setGifOn] = useState(false);
  const [gifOpen, setGifOpen] = useState(false);
  const [gifKind, setGifKind] = useState("sticker");
  const [gifQ, setGifQ] = useState("");
  const [gifResults, setGifResults] = useState([]);
  const [gifBusy, setGifBusy] = useState(false);

  // Recap templates (auto-montage from the user's own posts) + soundtrack
  const [recapTpls, setRecapTpls] = useState([]);
  const [music, setMusic] = useState([]);
  const [musicId, setMusicId] = useState(null);
  // where a picked GIF goes: overlay layer or a single collage sticker
  const [gifTarget, setGifTarget] = useState("overlay");
  const [collageGif, setCollageGif] = useState(null); // {url, preview, xf, yf, sizef}

  useEffect(() => {
    api.get("/media/filters").then((r) => setFilters(r.data.filters || [])).catch(() => {});
    api.get("/media/stickers").then((r) => setStickers(r.data.stickers || [])).catch(() => {});
    api.get("/media/gifs/config").then((r) => setGifOn(!!r.data.enabled)).catch(() => {});
    api.get("/media/recap/templates").then((r) => setRecapTpls(r.data.templates || [])).catch(() => {});
    api.get("/music/catalog?limit=40").then((r) => setMusic(r.data.items || [])).catch(() => {});
  }, []);

  const reset = () => { setResult(null); setCaption(""); setLayers([]); setSelId(null); setCollageGif(null); };
  const switchMode = (m) => { setMode(m); setFiles([]); reset(); setFilterKey("original"); setStrength(1); setRetouch(false); setGifOpen(false); };

  const onPick = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const single = mode === "overlay" || mode === "filter";
    const mapped = picked.map((f) => ({ url: URL.createObjectURL(f), file: f }));
    setFiles(single ? mapped.slice(0, 1) : [...files, ...mapped].slice(0, 7));
    reset();
    e.target.value = "";
  };

  // ---- actions ----
  const buildCollage = async () => {
    if (files.length < 2) return toast.error("Add at least 2 photos");
    setBusy(true);
    try {
      const form = new FormData();
      files.forEach((f, i) => form.append("files", f.file, `p${i}.jpg`));
      form.append("layout", layout);
      form.append("aspect", aspect);
      if (collageGif) {
        form.append("overlays", JSON.stringify([{ type: "gif", url: collageGif.url, x: collageGif.xf, y: collageGif.yf, size: collageGif.sizef }]));
      }
      const { data } = await api.post("/media/collage", form);
      setResult(data.media_type === "video" ? { path: data.video_path, type: "video" } : { path: data.image_path, type: "image" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Collage failed"); }
    finally { setBusy(false); }
  };

  const applyOverlay = async () => {
    if (!files[0]) return toast.error("Add a photo");
    if (!layers.length) return toast.error("Add text or a sticker");
    setBusy(true);
    try {
      const hasGif = layers.some((l) => l.type === "gif");
      const overlays = JSON.stringify(layers.map((l) =>
        l.type === "text" ? { type: "text", text: l.text, x: l.xf, y: l.yf, size: l.sizef, color: l.color, font: l.font }
          : l.type === "gif" ? { type: "gif", url: l.url, x: l.xf, y: l.yf, size: l.sizef }
            : { type: "sticker", emoji: l.emoji, x: l.xf, y: l.yf, size: l.sizef }));
      const form = new FormData();
      form.append("file", files[0].file, "base.jpg");
      form.append("overlays", overlays);
      if (hasGif) {
        // an animated sticker on a photo → auto-convert to a short clip
        form.append("is_image", "true");
        form.append("duration", "4");
        const { data } = await api.post("/media/video-compose", form);
        setResult({ path: data.video_path, type: "video" });
      } else {
        const { data } = await api.post("/media/photo-compose", form);
        setResult({ path: data.image_path, type: "image" });
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Overlay failed"); }
    finally { setBusy(false); }
  };

  const searchGifs = async (q, kind = gifKind) => {
    setGifBusy(true);
    try {
      const path = q && q.trim()
        ? `/media/gifs/search?q=${encodeURIComponent(q.trim())}&kind=${kind}&limit=24`
        : `/media/gifs/trending?kind=${kind}&limit=24`;
      const { data } = await api.get(path);
      setGifResults(data.items || []);
    } catch { setGifResults([]); }
    finally { setGifBusy(false); }
  };

  const renderSlideshow = async () => {
    if (files.length < 2) return toast.error("Add at least 2 photos");
    setBusy(true);
    try {
      const form = new FormData();
      files.forEach((f, i) => form.append("files", f.file, `p${i}.jpg`));
      form.append("per_photo_sec", String(perSec));
      form.append("aspect", aspect);
      form.append("transition", transition);
      const { data } = await api.post("/media/photo-to-video", form);
      setResult({ path: data.video_path, type: "video" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Render failed"); }
    finally { setBusy(false); }
  };

  const applyFilter = async () => {
    if (!files[0]) return toast.error("Add a photo");
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", files[0].file, "photo.jpg");
      form.append("filter", filterKey);
      form.append("retouch", retouch ? "true" : "false");
      form.append("strength", String(strength));
      const { data } = await api.post("/media/photo-filter", form);
      setResult({ path: data.image_path, type: "image" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Filter failed"); }
    finally { setBusy(false); }
  };

  const buildRecap = async (template) => {
    setBusy(true);
    try {
      const form = new FormData();
      form.append("template", template);
      if (musicId) form.append("music_id", musicId);
      const { data } = await api.post("/media/recap", form);
      setResult({ path: data.video_path, type: "video" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Post at least 2 photos first, then try again."); }
    finally { setBusy(false); }
  };

  const publish = async () => {
    if (!result) return;
    setBusy(true);
    try {
      await api.post("/posts", { caption, media_path: result.path, media_type: result.type, privacy: "public" });
      toast.success("Posted!");
      nav("/");
    } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
    finally { setBusy(false); }
  };

  // ---- overlay drag ----
  const addText = () => {
    const t = draft.trim();
    if (!t) { setAdding(false); return; }
    const id = `${Date.now()}`;
    setLayers((p) => [...p, { id, type: "text", text: t, color: "#FFFFFF", font: "poppins", xf: 0.5, yf: 0.4, sizef: 0.1 }]);
    setSelId(id);
    setDraft("");
    setAdding(false);
  };
  const addSticker = (emoji) => {
    const id = `${Date.now()}`;
    setLayers((p) => [...p, { id, type: "sticker", emoji, color: "#fff", font: "poppins", xf: 0.5, yf: 0.5, sizef: 0.18 }]);
    setSelId(id);
  };
  const addGif = (g) => {
    const id = `${Date.now()}`;
    setLayers((p) => [...p, { id, type: "gif", url: g.gif, preview: g.preview_webp || g.preview_gif, color: "#fff", font: "poppins", xf: 0.5, yf: 0.5, sizef: 0.3 }]);
    setSelId(id);
    setGifOpen(false);
  };
  const onGifPick = (g) => {
    if (gifTarget === "collage") {
      setCollageGif({ url: g.gif, preview: g.preview_webp || g.preview_gif, xf: 0.5, yf: 0.5, sizef: 0.32 });
      setGifOpen(false);
    } else {
      addGif(g);
    }
  };
  const openGifPicker = (target) => {
    setGifTarget(target);
    const nx = !(gifOpen && gifTarget === target);
    setGifOpen(nx);
    if (nx && !gifResults.length) searchGifs("");
  };
  const patchSel = (u) => setLayers((p) => p.map((l) => (l.id === selId ? { ...l, ...u } : l)));
  const removeSel = () => { setLayers((p) => p.filter((l) => l.id !== selId)); setSelId(null); };
  const onMove = (e) => {
    if (!dragId || !canvasRef.current) return;
    const r = canvasRef.current.getBoundingClientRect();
    const xf = Math.max(0, Math.min((e.clientX - r.left) / r.width, 1));
    const yf = Math.max(0, Math.min((e.clientY - r.top) / r.height, 1));
    setLayers((p) => p.map((l) => (l.id === dragId ? { ...l, xf, yf } : l)));
  };
  const canvasW = canvasRef.current?.clientWidth || 340;
  const sel = layers.find((l) => l.id === selId) || null;

  const btn = "px-4 py-2.5 rounded-full text-[11px] font-black tracking-[0.15em] uppercase border transition-colors";
  const chip = (on) => `${btn} ${on ? "bg-papaya text-ink border-papaya" : "bg-white/5 text-cream/70 border-white/10"}`;

  const renderGifPicker = () => (gifOn && gifOpen) ? (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-3 space-y-2" data-testid="ms-gif-picker">
      <div className="flex gap-2">
        {["sticker", "gif"].map((k) => (
          <button key={k} data-testid={`ms-gif-kind-${k}`} onClick={() => { setGifKind(k); searchGifs(gifQ, k); }} className={chip(gifKind === k)}>{k === "sticker" ? "Stickers" : "GIFs"}</button>
        ))}
      </div>
      <div className="flex items-center gap-2 rounded-2xl bg-white/5 border border-white/10 p-2">
        <input value={gifQ} onChange={(e) => setGifQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") searchGifs(gifQ); }} placeholder="Search GIPHY…" data-testid="ms-gif-search" className="flex-1 bg-transparent text-cream px-2 focus:outline-none text-sm" />
        <button onClick={() => searchGifs(gifQ)} data-testid="ms-gif-go" className="px-4 py-2 rounded-full bg-papaya text-ink text-[11px] font-black uppercase">Search</button>
      </div>
      <div className="grid grid-cols-4 gap-2 max-h-56 overflow-y-auto">
        {gifBusy
          ? <p className="col-span-4 text-center text-cream/40 text-xs py-4">Loading…</p>
          : gifResults.length
            ? gifResults.map((g) => (
              <button key={g.id} data-testid="ms-gif-result" onClick={() => onGifPick(g)} className="aspect-square rounded-lg overflow-hidden bg-white/10 flex items-center justify-center">
                <img src={g.preview_webp || g.preview_gif} alt={g.title} className="w-full h-full object-contain" />
              </button>))
            : <p className="col-span-4 text-center text-cream/40 text-xs py-4">No results</p>}
      </div>
      <p className="text-cream/30 text-[10px] text-right tracking-wide">Powered By GIPHY</p>
    </div>
  ) : null;

  return (
    <div className="min-h-[100dvh] bg-ink pb-32 px-5 pt-6" data-testid="media-studio">
      <div className="flex items-center gap-3">
        <button onClick={() => nav(-1)} className="w-10 h-10 rounded-full crystal-glass flex items-center justify-center text-cream" data-testid="ms-back"><ArrowLeft size={18} /></button>
        <div>
          <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Creator Studio</p>
          <h1 className="text-cream text-3xl font-black tracking-tighter">Media Studio</h1>
        </div>
      </div>

      <div className="mt-5 flex gap-2 overflow-x-auto pb-1">
        {MODES.map((m) => (
          <button key={m.k} data-testid={`ms-tab-${m.k}`} onClick={() => switchMode(m.k)} className={`flex-shrink-0 flex items-center gap-1.5 ${chip(mode === m.k)}`}>
            <m.icon size={14} weight="bold" /> {m.label}
          </button>
        ))}
      </div>

      <input ref={fileRef} type="file" accept="image/*" multiple={mode === "collage" || mode === "slideshow"} onChange={onPick} className="hidden" data-testid="ms-file" />

      {result ? (
        <section className="mt-6 rounded-3xl bg-white/5 border border-white/10 p-4" data-testid="ms-result">
          <div className="rounded-2xl overflow-hidden bg-black max-w-sm mx-auto" style={{ aspectRatio: result.type === "video" ? aspectRatio(aspect) : "auto" }}>
            {result.type === "video"
              ? <video src={fileUrl(result.path)} controls playsInline className="w-full h-full object-contain" data-testid="ms-result-video" />
              : <img src={fileUrl(result.path)} alt="" className="w-full h-full object-contain" data-testid="ms-result-image" />}
          </div>
          <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Add a caption…" data-testid="ms-caption" className="mt-3 w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-cream text-sm focus:outline-none focus:border-papaya" />
          <div className="flex gap-2 mt-3">
            <button onClick={reset} className="flex-1 py-3 rounded-full bg-white/5 border border-white/15 text-cream text-[11px] font-black tracking-[0.2em] uppercase" data-testid="ms-redo">Redo</button>
            <button onClick={publish} disabled={busy} className="flex-1 py-3 rounded-full bg-papaya text-ink text-[11px] font-black tracking-[0.2em] uppercase disabled:opacity-40" data-testid="ms-post">{busy ? "Posting…" : "Post"}</button>
          </div>
        </section>
      ) : (
        <>
          {/* photo tray */}
          {mode !== "recap" && (
          <section className="mt-6">
            <div className="flex flex-wrap gap-2">
              {files.map((f, i) => (
                <div key={i} className="relative w-20 h-28 rounded-xl overflow-hidden bg-white/5">
                  <img src={f.url} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => setFiles(files.filter((_, j) => j !== i))} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 flex items-center justify-center text-cream" data-testid={`ms-remove-${i}`}><X size={12} /></button>
                  <span className="absolute top-1 left-1 min-w-[18px] h-[18px] px-1 rounded-full bg-papaya text-ink text-[10px] font-black flex items-center justify-center">{i + 1}</span>
                </div>
              ))}
              {(files.length < 6 && !(mode === "overlay" || mode === "filter") || (files.length === 0)) && (
                <button onClick={() => fileRef.current?.click()} data-testid="ms-add" className="w-20 h-28 rounded-xl border border-dashed border-white/20 flex flex-col items-center justify-center text-papaya gap-1"><Plus size={22} /><span className="text-[10px] font-bold">Add</span></button>
              )}
            </div>
          </section>
          )}

          {mode === "recap" && (
            <section className="mt-6 space-y-3" data-testid="ms-recap">
              <p className="text-cream/60 text-sm">One tap turns your recent photo posts into a music-ready montage.</p>
              {music.length > 0 && (
                <div className="space-y-2">
                  <p className="text-cream/60 text-[10px] tracking-[0.25em] uppercase font-bold">Soundtrack</p>
                  <div className="flex gap-2 overflow-x-auto pb-1" data-testid="ms-recap-music">
                    <button onClick={() => setMusicId(null)} data-testid="ms-recap-music-none" className={`flex-shrink-0 ${chip(!musicId)}`}>No music</button>
                    {music.map((m) => (
                      <button key={m.id} data-testid={`ms-recap-music-${m.id}`} onClick={() => setMusicId(m.id)} className={`flex-shrink-0 ${chip(musicId === m.id)}`}>{m.title}</button>
                    ))}
                  </div>
                </div>
              )}
              {recapTpls.map((tpl) => (
                <button
                  key={tpl.key}
                  data-testid={`ms-recap-${tpl.key}`}
                  onClick={() => buildRecap(tpl.key)}
                  disabled={busy}
                  className="w-full flex items-center gap-3 rounded-3xl bg-white/5 border border-white/10 p-4 text-left disabled:opacity-40 hover:border-papaya/50 transition-colors"
                >
                  <div className="w-12 h-12 rounded-2xl bg-papaya/20 flex items-center justify-center text-2xl">{tpl.key === "monthly_recap" ? "📅" : "☀️"}</div>
                  <div className="flex-1">
                    <p className="text-cream font-black">{tpl.title}</p>
                    <p className="text-cream/70 text-xs">Last {tpl.window_days} day{tpl.window_days > 1 ? "s" : ""} · up to {tpl.max_photos} photos · {tpl.transition}</p>
                  </div>
                  <MagicWand size={20} className="text-papaya" weight="fill" />
                </button>
              ))}
              {busy && <p className="text-cream/40 text-xs text-center">Building your recap… this can take up to a minute.</p>}
            </section>
          )}

          {/* mode controls */}
          {mode === "collage" && (
            <section className="mt-5 space-y-3">
              <p className="text-cream/60 text-[10px] tracking-[0.25em] uppercase font-bold">Layout</p>
              <div className="flex gap-2">{LAYOUTS.map((l) => <button key={l} data-testid={`ms-layout-${l}`} onClick={() => setLayout(l)} className={chip(layout === l)}>{l}</button>)}</div>
              <p className="text-cream/60 text-[10px] tracking-[0.25em] uppercase font-bold">Aspect</p>
              <div className="flex gap-2">{ASPECTS.map((a) => <button key={a} data-testid={`ms-aspect-${a}`} onClick={() => setAspect(a)} className={chip(aspect === a)}>{a}</button>)}</div>
              {gifOn && (
                <>
                  <p className="text-cream/60 text-[10px] tracking-[0.25em] uppercase font-bold">Animated sticker</p>
                  {collageGif ? (
                    <div className="rounded-2xl bg-white/5 border border-white/10 p-3 space-y-3" data-testid="ms-collage-gif">
                      <div className="flex items-center gap-3">
                        <img src={collageGif.preview || collageGif.url} alt="" className="w-14 h-14 rounded-xl object-contain bg-black/30" />
                        <div className="flex-1">
                          <p className="text-cream text-sm font-bold">Sticker added</p>
                          <p className="text-cream/60 text-xs">Your collage will post as a looping clip.</p>
                        </div>
                        <button onClick={() => setCollageGif(null)} data-testid="ms-collage-gif-remove" className="text-red-400"><Trash size={18} /></button>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-cream/70 text-xs font-bold w-12">Size</span>
                        <input type="range" min="0.12" max="0.6" step="0.02" value={collageGif.sizef} onChange={(e) => setCollageGif({ ...collageGif, sizef: Number(e.target.value) })} data-testid="ms-collage-gif-size" className="flex-1 accent-papaya" />
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {[["Center", 0.5, 0.5], ["Top", 0.5, 0.18], ["Bottom", 0.5, 0.82], ["Left", 0.2, 0.5], ["Right", 0.8, 0.5]].map(([lbl, x, y]) => (
                          <button key={lbl} onClick={() => setCollageGif({ ...collageGif, xf: x, yf: y })} className={chip(collageGif.xf === x && collageGif.yf === y)}>{lbl}</button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => openGifPicker("collage")} data-testid="ms-collage-add-gif" className={`w-full flex items-center justify-center gap-1.5 ${btn} ${gifOpen && gifTarget === "collage" ? "bg-papaya text-ink border-papaya" : "bg-white/5 text-cream border-white/10"}`}><MagicWand size={14} weight="bold" /> Add animated sticker</button>
                  )}
                  {gifTarget === "collage" && renderGifPicker()}
                </>
              )}
              <button onClick={buildCollage} disabled={busy || files.length < 2} data-testid="ms-build" className="w-full py-4 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-sm flex items-center justify-center gap-2 disabled:opacity-40"><GridFour size={18} weight="fill" />{busy ? "Building…" : collageGif ? "Build clip" : "Build collage"}</button>
            </section>
          )}

          {mode === "slideshow" && (
            <section className="mt-5 space-y-3">
              <p className="text-cream/60 text-[10px] tracking-[0.25em] uppercase font-bold">Seconds / photo</p>
              <div className="flex gap-2">{[1.5, 2, 2.5, 3].map((n) => <button key={n} onClick={() => setPerSec(n)} className={chip(perSec === n)}>{n}s</button>)}</div>
              <p className="text-cream/60 text-[10px] tracking-[0.25em] uppercase font-bold">Aspect</p>
              <div className="flex gap-2">{ASPECTS.slice(0, 3).map((a) => <button key={a} data-testid={`ms-aspect-${a}`} onClick={() => setAspect(a)} className={chip(aspect === a)}>{a}</button>)}</div>
              <p className="text-cream/60 text-[10px] tracking-[0.25em] uppercase font-bold">Transition</p>
              <div className="flex gap-2">{TRANSITIONS.map((t) => <button key={t.k} data-testid={`ms-transition-${t.k}`} onClick={() => setTransition(t.k)} className={chip(transition === t.k)}>{t.l}</button>)}</div>
              <button onClick={renderSlideshow} disabled={busy || files.length < 2} data-testid="ms-render" className="w-full py-4 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-sm flex items-center justify-center gap-2 disabled:opacity-40"><FilmStrip size={18} weight="fill" />{busy ? "Rendering…" : "Render video"}</button>
            </section>
          )}

          {mode === "filter" && files[0] && (
            <section className="mt-5 space-y-3">
              <div className="rounded-2xl overflow-hidden bg-black max-w-xs mx-auto"><img src={files[0].url} alt="" className="w-full object-contain max-h-80" /></div>
              <div className="flex gap-2 overflow-x-auto pb-1">{filters.map((f) => <button key={f.key} data-testid={`ms-filter-${f.key}`} onClick={() => setFilterKey(f.key)} className={`flex-shrink-0 ${chip(filterKey === f.key)}`}>{f.label}</button>)}</div>
              <div className="flex items-center gap-3">
                <span className="text-cream/70 text-xs font-bold w-24">Strength {Math.round(strength * 100)}%</span>
                <input type="range" min="0" max="1" step="0.05" value={strength} onChange={(e) => setStrength(Number(e.target.value))} data-testid="ms-strength" className="flex-1 accent-papaya" />
              </div>
              <label className="flex items-center gap-2 text-cream/80 text-sm"><input type="checkbox" checked={retouch} onChange={(e) => setRetouch(e.target.checked)} data-testid="ms-retouch" className="accent-papaya" /> Smooth / retouch</label>
              <button onClick={applyFilter} disabled={busy} data-testid="ms-apply-filter" className="w-full py-4 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-sm flex items-center justify-center gap-2 disabled:opacity-40"><Sliders size={18} weight="fill" />{busy ? "Applying…" : "Apply filter"}</button>
            </section>
          )}

          {mode === "overlay" && files[0] && (
            <section className="mt-5 space-y-3">
              <div
                ref={canvasRef}
                className="relative rounded-2xl overflow-hidden bg-black max-w-xs mx-auto select-none touch-none"
                style={{ aspectRatio: "3 / 4" }}
                onPointerMove={onMove}
                onPointerUp={() => setDragId(null)}
                onPointerLeave={() => setDragId(null)}
              >
                <img src={files[0].url} alt="" className="absolute inset-0 w-full h-full object-cover" draggable={false} onClick={() => setSelId(null)} />
                {layers.map((l) => (
                  <div
                    key={l.id}
                    data-testid="ms-layer"
                    onPointerDown={(e) => { e.stopPropagation(); setSelId(l.id); setDragId(l.id); }}
                    style={{ position: "absolute", left: `${l.xf * 100}%`, top: `${l.yf * 100}%`, transform: "translate(-50%,-50%)", cursor: "move", outline: selId === l.id ? "1px dashed #C66A4A" : "none", padding: 2 }}
                  >
                    {l.type === "text"
                      ? <span style={{ color: l.color, fontWeight: 900, fontSize: l.sizef * canvasW, textShadow: "0 1px 4px rgba(0,0,0,0.5)", whiteSpace: "pre" }}>{l.text}</span>
                      : l.type === "gif"
                        ? <img src={l.preview || l.url} alt="" draggable={false} style={{ width: l.sizef * canvasW, height: "auto", pointerEvents: "none" }} />
                        : <span style={{ fontSize: l.sizef * canvasW }}>{l.emoji}</span>}
                  </div>
                ))}
              </div>

              {sel && (
                <div className="rounded-2xl bg-white/5 border border-white/10 p-3 space-y-2">
                  <div className="flex items-center gap-3">
                    <span className="text-cream/70 text-xs font-bold w-12">Size</span>
                    <input type="range" min="0.04" max="0.32" step="0.01" value={sel.sizef} onChange={(e) => patchSel({ sizef: Number(e.target.value) })} className="flex-1 accent-papaya" />
                    <button onClick={removeSel} data-testid="ms-layer-delete" className="text-red-400"><Trash size={18} /></button>
                  </div>
                  {sel.type === "text" && (
                    <>
                      <div className="flex gap-2">{COLORS.map((c) => <button key={c} onClick={() => patchSel({ color: c })} className="w-7 h-7 rounded-full border-2" style={{ backgroundColor: c, borderColor: sel.color === c ? "#C66A4A" : "transparent" }} />)}</div>
                      <div className="flex gap-2">{FONTS.map((f) => <button key={f.k} onClick={() => patchSel({ font: f.k })} className={chip(sel.font === f.k)}>{f.l}</button>)}</div>
                    </>
                  )}
                </div>
              )}

              <div className="flex gap-2">
                <button onClick={() => { setAdding(true); setSelId(null); }} data-testid="ms-add-text" className={`flex-1 flex items-center justify-center gap-1.5 ${btn} bg-white/5 text-cream border-white/10`}><TextT size={14} weight="bold" /> Add text</button>
                {gifOn && (
                  <button onClick={() => openGifPicker("overlay")} data-testid="ms-add-gif" className={`flex-1 flex items-center justify-center gap-1.5 ${btn} ${gifOpen && gifTarget === "overlay" ? "bg-papaya text-ink border-papaya" : "bg-white/5 text-cream border-white/10"}`}><MagicWand size={14} weight="bold" /> GIFs / Animated</button>
                )}
              </div>
              {gifTarget === "overlay" && renderGifPicker()}
              {adding && (
                <div className="flex items-center gap-2 rounded-2xl bg-white/5 border border-white/10 p-2">
                  <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addText(); }} placeholder="Type text…" data-testid="ms-text-input" className="flex-1 bg-transparent text-cream px-2 focus:outline-none text-sm" />
                  <button onClick={addText} data-testid="ms-text-confirm" className="px-4 py-2 rounded-full bg-papaya text-ink text-[11px] font-black uppercase">Add</button>
                </div>
              )}
              <div className="flex gap-2 overflow-x-auto pb-1">
                {stickers.map((st) => <button key={st.key} data-testid={`ms-sticker-${st.key}`} onClick={() => addSticker(st.emoji)} className="flex-shrink-0 w-12 h-12 rounded-xl bg-white/5 border border-white/10 text-2xl flex items-center justify-center">{st.emoji}</button>)}
              </div>
              <button onClick={applyOverlay} disabled={busy} data-testid="ms-apply-overlay" className="w-full py-4 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-sm flex items-center justify-center gap-2 disabled:opacity-40"><MagicWand size={18} weight="fill" />{busy ? "Applying…" : "Apply overlays"}</button>
            </section>
          )}

          {(mode === "overlay" || mode === "filter") && !files[0] && (
            <p className="text-cream/40 text-sm mt-6 text-center">Add a photo to start.</p>
          )}
        </>
      )}
    </div>
  );
}
